import { useEffect, useRef } from 'react';
import { Action, History, Location } from 'history';
import { Composition } from '../types/api/Composition';
import { State } from '../application/browser/browser.utils';
import { getScrollPosition, saveScrollPosition } from './scrollPosition';
import { GlobalDocumentIds } from '../types/GlobalDocumentIds';

/**
 * As our SPA navigation is not optimal, we need to reimplement the scroll restoration feature.
 * see: SON-11194
 * Restores the scroll position after back/forward navigation and reloads (action 'POP'),
 * starts at the top on fresh visits and link clicks (action 'PUSH'/'REPLACE') and
 * scrolls to the text mark (hash) if the location has one.
 * @param loc location of the new page
 * @param action navigation action that led to this location
 */
const handleScrollPositionAfterPageNavigation = (loc: Location, action: Action) => {
  if (loc.hash) {
    document.getElementById(loc.hash.replace('#', ''))?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  } else if (action === Action.Pop) {
    document.getElementById(GlobalDocumentIds.scrollContent)?.scrollTo({ top: getScrollPosition(loc) });
  } else {
    document.getElementById(GlobalDocumentIds.scrollContent)?.scrollTo({ top: 0 });
  }
};

/**
 * Separate to scroll position, we have to handle where focus will land after navigation.
 * Usually its the h1, as per w3c standard.
 * Exceptions:
 *  - h2 of profile edit page, else users will not notice our hint which is above our h1
 */
const handleFocusAfterPageNavigation = () => {
  const personProfileEditHint: HTMLHeadingElement | null = document.querySelector(
    'h2[id^="widget-header-personprofileedithint-"]'
  );
  if (personProfileEditHint) {
    personProfileEditHint.setAttribute('tabindex', '-1');
    personProfileEditHint.focus({ preventScroll: true });
  } else {
    document.getElementById(GlobalDocumentIds.pageTitle)?.focus({ preventScroll: true });
  }
};

/**
 * Determines how the browser has loaded the document:
 * - 'reload' | 'back_forward' => the scroll position should be restored ('POP' semantics)
 * - anything else ('navigate', 'prerender') => fresh visit, the page should start at the top ('PUSH' semantics)
 * BEWARE: We intentionally only compare against 'reload' and 'back_forward' here, because the TS lib types
 * differ on whether NavigationTimingType contains 'prerender' (TS2367 in older lib.dom versions), so an
 * explicit 'prerender' comparison would not compile across all TS versions.
 */
const getInitialNavigationAction = (): Action => {
  if (typeof performance.getEntriesByType !== 'function') {
    return Action.Push;
  }
  const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  const navigationType = navigationEntry?.type ?? 'navigate';
  return navigationType === 'reload' || navigationType === 'back_forward' ? Action.Pop : Action.Push;
};

/**
 * Orchestrates the scroll position handling of the portal:
 * - saves the scroll position of the leaving page on every SPA navigation and on beforeunload
 * - restores or resets the scroll position once the new composition has been rendered
 * - restores the scroll position on reload and back/forward navigation, starts at the top
 *   on fresh visits and link clicks, and scrolls to the text mark (hash) if the location has one
 * - focuses the <h1> after navigation (not on real refreshes or links opened in a new window)
 *
 * Testing (see ScrollPosition.cy.js, FocusingHeadlineOnPageRefresh.cy.js):
 * - Homepage: scroll down to a link => click => go back => scroll position is where you left, focus is on <h1>.
 * - Reload: scroll position moves to where you left, focus is not on <h1>.
 * - Open Link in new window => tab once => first skip link is focused.
 * - Fresh visit of a page with a saved scroll position => starts at the top.
 */
export function useScrollRestoration(
  history: History,
  state: State,
  composition: Composition,
  fetchedComposition: Composition
) {
  const lastNavigationActionRef = useRef<Action>(getInitialNavigationAction());
  const lastPathnameRef = useRef<string | null>(null);

  // remember the navigation action and save the scroll position of the leaving page
  useEffect(() => {
    return history.listen(event => {
      lastNavigationActionRef.current = event.action;
      saveScrollPosition(state.location);
    });
  }, [history, state.location]);

  // save the scroll position of the current page when the page is unloaded (reload, closing the tab)
  useEffect(() => {
    const saveHandler = () => {
      saveScrollPosition(state.location);
    };
    window.addEventListener('beforeunload', saveHandler);
    return () => {
      window.removeEventListener('beforeunload', saveHandler);
    };
  }, [state.location]);

  // restore or reset the scroll position once the new composition has been rendered
  useEffect(() => {
    const isInitialRun = lastPathnameRef.current === null;
    const isPathChanged = !isInitialRun && lastPathnameRef.current !== state.location.pathname;
    lastPathnameRef.current = state.location.pathname;

    // only touch the scroll position on real page changes, not on query-only navigations (e.g. filters)
    if (isInitialRun || isPathChanged) {
      handleScrollPositionAfterPageNavigation(state.location, lastNavigationActionRef.current);
    }
    // focusing h1 only, if we are not doing a real refresh like reload or link open in new window
    if (fetchedComposition !== composition) {
      handleFocusAfterPageNavigation();
    }
    // state.location is not included in dependency array here, because of scroll position issues!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composition, fetchedComposition]);
}
